<template>
  <div
    ref="mediaElement"
    class="media-element"
    :class="{ 'media-element--background' : !inlineMedia }"
  >
    <component
      :is="componentType"
      class="media-element__media"
      v-bind="props"
      @set-aspect-ratio="setAspectRatio"
    />
  </div>
</template>

<script>

/**
 * Component: Media Element
 * ------------------------------------------------------------------------------
 * - Component which will conditionally render an image or video component
 *   depending on the users inputted settings.
 * @param {Boolean} [autoplayVideo] - Autoplay a background video.
 * @param {Boolean} [loopVideo] - Loop a background video. (optional)
 * @param {String} [alt] - Override default alt text
 * element instead of background
 * @param {Boolean} [inlineMedia] - Display the media as an inline
 * element instead of background
 * @param {Object} image - A formatted image object.
 * @param {Object} [imageDesktop] - A formatted image object - specific to desktop. (>= 1024px)
 * @param {Boolean} lazyload - Lazyload the image - defaulting to `true`
 * @param {Object} video - A formatted image object.
 * @param {Object} [videoDesktop] - A formatted video object - specific to desktop. (>= 1024px)
 * @param {String} [videoExternalUrl] - An external video URL
 * @param {String} [videoExternalDesktopUrl] - An external video URL (desktop)
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'
import ImageElement from '~components/image-element/image-element'
import VideoElement from '~components/video-element/video-element'

export default {
  name: 'MediaElement',

  components: {
    ImageElement,
    VideoElement,
  },

  props: {
    autoplayVideo: {
      type: Boolean,
      default: false,
    },
    loopVideo: {
      type: Boolean,
      default: null,
      required: false,
    },
    alt: {
      type: String,
      default: '',
    },
    image: {
      type: [Boolean, Object],
      default: false,
    },
    imageDesktop: {
      type: [Boolean, Object],
      default: false,
    },
    inlineMedia: {
      type: Boolean,
      default: false,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    video: {
      type: [Boolean, Object],
      default: false,
    },
    videoDesktop: {
      type: [Boolean, Object],
      default: false,
    },
    videoExternalUrl: {
      type: String,
      default: '',
    },
    videoExternalDesktopUrl: {
      type: String,
      default: '',
    },
  },

  emits: ['set-aspect-ratio'],

  data() {
    return {
      displayedVideo: false,
      height: 0,
      videoKey: `video-${new Date().toISOString()}`,
      width: 0,
    }
  },

  computed: {
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the component to render.
     * - Default to image, show video if populated.
     * - Additional check added for:
     *     No mobile video object
     *     Has a desktop video object
     *     Is mobile viewport
     *   Will now return image, will render default image on mobile.
     * @returns {String}
     */
    componentType() {
      const hasDesktopVideo = this.videoDesktop || this.videoExternalDesktopUrl
      const hasVideo = this.video || this.videoExternalUrl

      if (!hasVideo) {
        if (hasDesktopVideo && this.isMobile) {
          return 'image-element'
        }
      }

      if (hasVideo || hasDesktopVideo) {
        return 'video-element'
      }

      return 'image-element'
    },

    /**
     * Calculate the props for the component.
     * @returns {Boolean|Object}
     */
    props() {
      if (this.componentType === 'image-element') {
        return {
          alt: this.alt,
          inlineImage: this.inlineMedia,
          image: this.image,
          imageDesktop: this.imageDesktop,
          lazyload: this.lazyload,
        }
      }

      if (this.componentType === 'video-element') {
        return {
          autoplay: this.autoplayVideo,
          loop: this.loopVideo,
          inlinePlayer: this.inlineMedia,
          video: this.video,
          videoDesktop: this.videoDesktop,
          videoExternalUrl: this.videoExternalUrl,
          videoExternalDesktopUrl: this.videoExternalDesktopUrl,
        }
      }

      return false
    },
  },

  methods: {

    /**
     * Set the CSS aspect ratio
     * - Runs after child component event.
     * @param {String} aspectRatio - Valid CSS aspect ratio
     */
    setAspectRatio(aspectRatio) {
      if (!aspectRatio) {
        return
      }

      this.$refs.mediaElement.style.setProperty(
        '--aspect-ratio',
        aspectRatio,
      )

      this.$emit('set-aspect-ratio', aspectRatio)
    },
  },
}
</script>
